/**
 * Sentry for the React frontend — errors only, production only.
 *
 * Until 2026-07-28 the browser had NOTHING listening: an uncaught render error
 * (e.g. the dangling setter that made the deal modal unclosable, ff22c60)
 * existed only in the operator's console and reached no one. The worker has
 * @sentry/cloudflare; this is the missing half.
 *
 * Deliberately narrow:
 *   - `enabled` only in production builds — local dev and vitest never send.
 *   - No tracing, no session replay. The point is "did it crash", not
 *     performance telemetry; keep quota for the signal we need.
 *   - `captureConsoleIntegration(['error'])` mirrors the worker's config, so
 *     `console.error` calls (the codebase's existing failure idiom, e.g.
 *     "second-opinion POST error:") become events too, not just uncaught
 *     throws.
 *
 * The DSN is a build-time constant, not a secret — a DSN only lets you SEND
 * events, and it ships in the bundle by design. Empty DSN = Sentry disabled,
 * same convention as the worker (`env.SENTRY_DSN || ''`).
 *
 * SHARED PROJECT (operator decision 2026-07-28, option B): this is the
 * `cloudflare-workers` project's DSN — org admin rights are needed to create a
 * `prism-frontend` project and none were to hand. Browser and worker events
 * therefore land in ONE issue stream; the `app: prism-frontend` tag is what
 * tells them apart, so alert rules can filter on it. If a dedicated project is
 * ever created, swap this constant and delete this paragraph.
 */
import * as Sentry from '@sentry/react';

const SENTRY_DSN = 'https://7edc4bf1476a31e9f905d47662a6daf3@o4510911752962048.ingest.de.sentry.io/4510912347373648';

export function initSentry() {
    Sentry.init({
        dsn: SENTRY_DSN,
        enabled: Boolean(SENTRY_DSN) && import.meta.env.PROD,
        environment: 'production',
        initialScope: { tags: { app: 'prism-frontend' } },
        integrations: [
            Sentry.captureConsoleIntegration({ levels: ['error'] }),
        ],
        // Browser-extension and embedded-webview noise, not our code.
        ignoreErrors: [
            /extension\//i,
            /^chrome:\/\//,
            /^moz-extension:\/\//,
        ],
    });
}

export { Sentry };
